import React, { useCallback, useEffect, useMemo } from 'react';

import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import { fg } from '@atlaskit/platform-feature-flags';
import { stripMatchingOuterQuotes } from '@atlassian/ai-model-io/utils/strip-matching-outer-quotes';
import { createUnifiedAnalyticsPayload } from '@atlassian/editor-ai-common/analytics/create-unified-analytics-payload';
import { AcceptRejectScreen } from '@atlassian/generative-ai-modal/screens/AcceptReject';

import { type StreamFns } from '../../../../pm-plugins/in-editor-direct-streaming/hooks/useInEditorDirectStreaming';
import type { ResponseFeedbackButtonClickAEP } from '../../../../types/analytics';
import type { BackendModel } from '../../../../types/prompt-request-types';
import type { FeedbackMetadata, PluginFeedbackMetadata } from '../../../../types/types';
import { useAIExperienceCommonDataContext } from '../../../hooks/useAIExperienceCommonData';
import { useContentComponentContext } from '../../../hooks/useContentComponentContext';
import { useLocalizedLatestPrompt } from '../../../hooks/useLocalizedLatestPrompt';
import { isConfluenceTitleToolbarSuggestTitle } from '../../../prebuilt/config-items/utils/suggest-title';
import { convertExperienceName } from '../../../utils/analytics/analyticsFlow';
import { useAnalyticsFlow } from '../../../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../../../utils/analytics/utils';
import { MAX_DIFF_RATIO, calculateDiffRatio } from '../../../utils/calculateDiffRatio';
import type { AIExperienceMachineContext } from '../../../utils/xstate/get-ai-experience-service';

interface AcceptRejectScreenWithLogicProps {
	aiExperienceMachineContext: AIExperienceMachineContext;
	backendModel: BackendModel | undefined;
	discardStream: StreamFns['discardStream'];
}

export const AcceptRejectScreenWithLogic = (props: AcceptRejectScreenWithLogicProps) => {
	const { aiExperienceMachineContext, backendModel, discardStream } = props;
	const {
		configItem,
		channelId,
		responseHistory: history,
		latestPrompt,
		markdown: actualMarkdown,
		retryPromptCount,
		endExperience,
	} = aiExperienceMachineContext;
	const { api } = useContentComponentContext();
	const { appearance, editorPluginAIProvider, lastTriggeredFrom, sendToAIExperienceMachine } =
		useAIExperienceCommonDataContext();
	const product = editorPluginAIProvider.product;
	// preset prompt or base generate / interrogation
	const localizedLatestPrompt = useLocalizedLatestPrompt(latestPrompt);
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const analyticsFlow = useAnalyticsFlow();
	const lastHistoryEvent = history.entries[history.positionFromEnd];
	/**
	 * We may remove this diff ratio calculation, when we are sure that the backend
	 * calculation is working. We will need to have converted to the new ConvoAI endpoint.
	 * @see https://hello.jira.atlassian.cloud/browse/AIX-1665
	 * @see https://hello.jira.atlassian.cloud/browse/EDITOR-792
	 */
	const diffRatio =
		(lastHistoryEvent.modelInput.selection || lastHistoryEvent.modelInput.doc) &&
		actualMarkdown &&
		fg('editor_ai_no_improvement_deadend_fix')
			? calculateDiffRatio({
					input: lastHistoryEvent.modelInput.selection || lastHistoryEvent.modelInput.doc || '',
					output: actualMarkdown,
				})
			: 1;
	const responseTooSimilar = lastHistoryEvent.responseTooSimilar || diffRatio < MAX_DIFF_RATIO;
	const inputOutputDiffRatio = lastHistoryEvent.inputOutputDiffRatio;
	const aiExperienceMetadata = useMemo(() => {
		return {
			configItemTitle: configItem.agent?.name || convertExperienceName(configItem.key.toString()),
			lastTriggeredFrom,
			inputOutputDiffRatio,
			backendModel,
		};
	}, [configItem, lastTriggeredFrom, inputOutputDiffRatio, backendModel]);
	let markdown: string = actualMarkdown || '';
	if (markdown && isConfluenceTitleToolbarSuggestTitle({ configItem, lastTriggeredFrom })) {
		markdown = stripMatchingOuterQuotes(markdown);
	}

	useEffect(() => {
		// turn diff on, diff will be turned off when unmount ExperienceApplication
		if (!api?.trackChanges?.sharedState.currentState()?.isDisplayingChanges) {
			api?.core.actions.execute(api?.trackChanges?.commands.toggleChanges);
		}
	}, [api]);

	const onAccept = useCallback(() => {
		api?.analytics?.actions.fireAnalyticsEvent({
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.BUTTON,
			actionSubjectId: ACTION_SUBJECT_ID.AI_CHANGES_ACCEPT_BUTTON,
			eventType: EVENT_TYPE.UI,
			attributes: {
				appearance,
				configItemTitle: configItem.agent?.name || configItem.key,
				lastTriggeredFrom,
				product,
			},
		});
		api?.analytics?.actions.fireAnalyticsEvent(
			createUnifiedAnalyticsPayload({
				action: ACTION.ACTIONED,
				interactionID: channelId ?? analyticsFlow.getLastAiInteractionId(),
				experienceName: convertExperienceName(configItem.key.toString()),
				isProactive: false,
				attributes: {
					...analyticsFlow.getAiLifeCycleDynamicAttributes({ init: false }),
					aiResultAction: 'accepted',
				},
				invokedFrom: lastTriggeredFrom,
			}),
		);
		endExperience();
	}, [
		api,
		appearance,
		configItem,
		lastTriggeredFrom,
		product,
		endExperience,
		channelId,
		analyticsFlow,
	]);

	const onReject = useCallback(() => {
		api?.analytics?.actions.fireAnalyticsEvent({
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.BUTTON,
			actionSubjectId: ACTION_SUBJECT_ID.AI_CHANGES_REJECT_BUTTON,
			eventType: EVENT_TYPE.UI,
			attributes: {
				appearance,
				configItemTitle: configItem.agent?.name || configItem.key,
				lastTriggeredFrom,
				product,
			},
		});
		api?.analytics?.actions.fireAnalyticsEvent(
			createUnifiedAnalyticsPayload({
				action: ACTION.DISMISSED,
				interactionID: channelId ?? analyticsFlow.getLastAiInteractionId(),
				experienceName: convertExperienceName(configItem.key.toString()),
				isProactive: false,
				attributes: analyticsFlow.getAiLifeCycleDynamicAttributes({ init: false }),
				invokedFrom: lastTriggeredFrom,
			}),
		);
		discardStream?.();
		endExperience();
	}, [
		api,
		appearance,
		configItem,
		lastTriggeredFrom,
		product,
		discardStream,
		endExperience,
		channelId,
		analyticsFlow,
	]);

	const fireResponseFeedbackEvent = useCallback(
		(
			sentiment: FeedbackMetadata['sentiment'],
			productSuccessState: ResponseFeedbackButtonClickAEP['attributes']['productSuccessStatus'],
		) => {
			fireAIAnalyticsEvent({
				payload: {
					action: 'click',
					actionSubject: 'editorPluginAI',
					actionSubjectId: 'responseFeedbackButton',
					attributes: {
						sentiment,
						appearance,
						product,
						configItemTitle: configItem.agent?.name || configItem.key,
						lastTriggeredFrom,
						productSuccessStatus: productSuccessState,
						inputOutputDiffRatio,
						retryPromptCount,
						responseTooSimilar,
						aiInteractionID: analyticsFlow.getLastAiInteractionId(),
					},
					eventType: EVENT_TYPE.UI,
				} as ResponseFeedbackButtonClickAEP,
			});

			fireAIAnalyticsEvent({
				payload: createUnifiedAnalyticsPayload({
					action: ACTION.SUBMITTED,
					interactionID: channelId ?? analyticsFlow.getLastAiInteractionId(),
					experienceName: convertExperienceName(configItem.key.toString(), lastTriggeredFrom),
					isProactive: false,
					invokedFrom: lastTriggeredFrom,
					attributes: {
						aiFeedbackResult: sentiment === 'good' ? 'up' : 'down',
						...analyticsFlow.getAiLifeCycleDynamicAttributes({ init: false }),
					},
				}),
			});
		},
		[
			configItem,
			fireAIAnalyticsEvent,
			analyticsFlow,
			channelId,
			lastTriggeredFrom,
			inputOutputDiffRatio,
			retryPromptCount,
			responseTooSimilar,
			appearance,
			product,
		],
	);

	const handleFiringFeedbackEvent = useCallback(
		async (feedbackMetadata: FeedbackMetadata) => {
			analyticsFlow.addAttributes({
				userFeedbackSentiment: feedbackMetadata.sentiment,
			});

			// we need to update the state machine to reflect the user feedback
			sendToAIExperienceMachine({
				type: 'feedback submitted',
				sentiment: feedbackMetadata.sentiment as 'good' | 'bad',
			});

			// handleFeedbackSubmission is intended to be implemented
			// by all products -- there will be a period where products won't have
			// it implemented (and will be relying on THUMBS_UP/THUMBS_DOWN)
			// in these cases -- we don't know the product response status
			if (!editorPluginAIProvider.handleFeedbackSubmission) {
				fireResponseFeedbackEvent(feedbackMetadata.sentiment, 'unknown');
				return;
			} else {
				try {
					const productResponse =
						await editorPluginAIProvider.handleFeedbackSubmission(feedbackMetadata);
					fireResponseFeedbackEvent(feedbackMetadata.sentiment, productResponse.status);
				} catch {
					fireResponseFeedbackEvent(feedbackMetadata.sentiment, 'failed');
				}
			}
		},
		[fireResponseFeedbackEvent, editorPluginAIProvider, sendToAIExperienceMachine, analyticsFlow],
	);

	const getAIExperience: PluginFeedbackMetadata['getAIExperience'] = useCallback(
		(hasUserConsent?: boolean) => ({
			...aiExperienceMetadata,
			...(hasUserConsent && {
				userPrompt: localizedLatestPrompt.userInput,
				markdownResponse: markdown,
			}),
		}),
		[aiExperienceMetadata, localizedLatestPrompt, markdown],
	);

	const onBadResponseButtonClick = useCallback(() => {
		const feedbackMetadata = {
			sentiment: 'bad' as const,
			getAIExperience,
			editorAttributes: { appearance, product },
		};

		// Now deprecated - migrate to handleFeedbackSubmission
		editorPluginAIProvider.onExperienceEvent &&
			editorPluginAIProvider.onExperienceEvent.THUMBS_DOWN();

		handleFiringFeedbackEvent(feedbackMetadata);
	}, [
		getAIExperience,
		editorPluginAIProvider.onExperienceEvent,
		handleFiringFeedbackEvent,
		appearance,
		product,
	]);

	const onGoodResponseButtonClick = useCallback(() => {
		const feedbackMetadata = {
			sentiment: 'good' as const,
			getAIExperience,
			editorAttributes: { appearance, product },
		};

		// Now deprecated - migrate to handleFeedbackSubmission
		editorPluginAIProvider.onExperienceEvent &&
			editorPluginAIProvider.onExperienceEvent.THUMBS_UP();

		handleFiringFeedbackEvent(feedbackMetadata);
	}, [
		getAIExperience,
		editorPluginAIProvider.onExperienceEvent,
		handleFiringFeedbackEvent,
		appearance,
		product,
	]);

	return (
		<AcceptRejectScreen
			onAccept={onAccept}
			onReject={onReject}
			onBadResponseButtonClick={onBadResponseButtonClick}
			onGoodResponseButtonClick={onGoodResponseButtonClick}
		/>
	);
};
